import { ArrowRight, CalendarClock, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { StatusBadge, type StatusTone } from "@/components/status-badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { DashboardRecentLoad } from "@/features/dashboard/api/types"
import { formatDate, formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

function badgeFor(load: DashboardRecentLoad): { tone: StatusTone; key: string } {
  if (load.status === "PENDING") {
    return { tone: "warning", key: `recentLoads.status.PENDING` }
  }
  if (load.status === "COMPLETED") {
    if (load.decision === "REJECTED") {
      return { tone: "danger", key: `recentLoads.decision.REJECTED` }
    }
    if (load.decision === "ACCEPTED") {
      return { tone: "success", key: `recentLoads.decision.ACCEPTED` }
    }
    return { tone: "success", key: `recentLoads.status.COMPLETED` }
  }
  return { tone: "info", key: `recentLoads.status.${load.status}` }
}

function titleFor(load: DashboardRecentLoad): string {
  if (load.kind === "pending") {
    return load.billOfLading ?? `#${load.id}`
  }
  if (load.loadNumber) {
    return String(load.loadNumber)
  }
  if (load.bordereauNumber) {
    return String(load.bordereauNumber)
  }
  return `#${load.id}`
}

interface LoadRowProps {
  load: DashboardRecentLoad
}

function LoadRow({ load }: LoadRowProps) {
  const { t } = useTranslation("dashboard")
  const badge = badgeFor(load)
  const title = titleFor(load)
  const netMt = load.netKg != null ? formatNumber(kgToMt(load.netKg), 1) : null
  const to =
    load.kind === "pending" ? paths.reception : paths.receptionDetail(load.id)

  return (
    <Link
      to={to}
      className="hover:bg-muted/40 group -mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold">{title}</p>
          <StatusBadge tone={badge.tone}>{t(badge.key)}</StatusBadge>
        </div>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
          {load.cooperativeName ? (
            <span className="truncate">{load.cooperativeName}</span>
          ) : null}
          {load.contractNumber ? (
            <span className="truncate">· {load.contractNumber}</span>
          ) : null}
          {load.plateNumber ? (
            <span className="inline-flex items-center gap-1 font-mono tabular-nums">
              <Truck className="size-3.5" />
              {load.plateNumber}
            </span>
          ) : null}
          {load.arrivedAt ? (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <CalendarClock className="size-3.5" />
              {t("recentLoads.arrivedOn", { date: formatDate(load.arrivedAt) })}
            </span>
          ) : null}
        </div>
      </div>
      <div className="text-right">
        {netMt ? (
          <p className="text-base font-semibold leading-tight tabular-nums">
            {netMt} <span className="text-muted-foreground text-xs">MT</span>
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">—</p>
        )}
      </div>
      <ArrowRight className="text-muted-foreground/50 group-hover:text-muted-foreground size-4 shrink-0 transition-colors" />
    </Link>
  )
}

interface RecentLoadsProps {
  loads: DashboardRecentLoad[] | null | undefined
}

export function RecentLoads({ loads }: RecentLoadsProps) {
  const { t } = useTranslation("dashboard")
  const items = loads ?? []

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{t("recentLoads.title")}</CardTitle>
        <Link
          to={paths.reception}
          className="text-primary inline-flex items-center gap-1 text-xs font-medium hover:underline"
        >
          {t("recentLoads.viewAll")}
          <ArrowRight className="size-3.5" />
        </Link>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <EmptyState
            icon={<Truck className="size-6" />}
            title={t("recentLoads.empty.title")}
            description={t("recentLoads.empty.description")}
            className="py-6"
          />
        ) : (
          <ul className="divide-border flex flex-col divide-y">
            {items.map((l) => (
              <li key={l.id}>
                <LoadRow load={l} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
